import { motion, useInView } from 'framer-motion';
import { useRef, useEffect, useState, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { RevealImage } from './RevealImage';

function getVideoEmbedInfo(url) {
  if (!url) return null;
  const ytPatterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pat of ytPatterns) {
    const m = url.match(pat);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&loop=1&controls=0&playlist=${m[1]}&modestbranding=1&showinfo=0&rel=0`;
  }
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1`;
  return url;
}

function MediaBlock({ item, index }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const isEven = index % 2 === 0;
  const hasGoldBand = index === 0 || index === 2;

  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const getOptimizedUrl = (item) => resolveUrl(item?.optimized_url || item?.url);
  const getThumbUrl = (item) => resolveUrl(item?.thumb_url || item?.url);

  const isUploadedVideo = item.media_type === 'video' && item.url && !item.video_url;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      if (!isSeeking && video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [isSeeking]);

  const handleSoundToggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isMuted) {
      video.muted = false;
      video.currentTime = 0;
      video.play().catch(() => {});
      setIsMuted(false);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  }, [isMuted]);

  const handleSeek = useCallback((e) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const val = parseFloat(e.target.value);
    video.currentTime = (val / 100) * video.duration;
    setProgress(val);
  }, []);

  return (
    <div className="relative" ref={ref}>
      {/* Gold cinematic band — videos 1 & 3 only */}
      {hasGoldBand && (
        <motion.div
          initial={{ opacity: 0, scaleX: 0.7 }}
          animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="absolute inset-0 -inset-x-6 md:-inset-x-12 lg:-inset-x-24 -inset-y-8 md:-inset-y-12 z-0 origin-center"
          style={{
            background: 'linear-gradient(180deg, rgba(120,90,20,0.18) 0%, rgba(100,75,15,0.22) 50%, rgba(120,90,20,0.18) 100%)',
          }}
        />
      )}
      <motion.div
        initial={{ opacity: 0, y: 70 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`relative z-[1] flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-20 items-center`}
      >
      <div
        className="w-full lg:w-2/3 group relative overflow-hidden media-glow"
        onMouseEnter={() => isUploadedVideo && setShowControls(true)}
        onMouseLeave={() => isUploadedVideo && setShowControls(false)}
      >
        <div className="aspect-[16/10] overflow-hidden bg-surface relative">
          {item.video_url ? (
            <iframe
              src={getVideoEmbedInfo(item.video_url)}
              className="w-full h-full"
              allow="autoplay; fullscreen"
              title={item.title}
              loading="lazy"
            />
          ) : isUploadedVideo ? (
            <>
              <video
                ref={videoRef}
                src={resolveUrl(item.url)}
                className="w-full h-full object-cover"
                preload="auto"
                muted
                loop
                playsInline
                data-testid={`media-story-video-${index}`}
              />

              {/* Sound button — top right */}
              <button
                onClick={handleSoundToggle}
                className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border border-white/10 hover:border-gold/40 hover:bg-black/80 transition-all duration-300 cursor-pointer"
                data-testid={`media-story-sound-${index}`}
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 text-white/70" />
                ) : (
                  <Volume2 className="w-4 h-4 text-gold" />
                )}
              </button>

              {/* Seek slider — bottom */}
              <div
                className={`absolute bottom-0 left-0 right-0 z-10 px-4 pb-3 pt-8 bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-300 ${showControls || !isMuted ? 'opacity-100' : 'opacity-0'}`}
              >
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={progress}
                  onChange={handleSeek}
                  onMouseDown={() => setIsSeeking(true)}
                  onMouseUp={() => setIsSeeking(false)}
                  onTouchStart={() => setIsSeeking(true)}
                  onTouchEnd={() => setIsSeeking(false)}
                  className="w-full h-1 appearance-none bg-white/20 rounded-full cursor-pointer outline-none
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-[0_0_6px_rgba(191,155,48,0.5)]
                    [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:border-0"
                  style={{ background: `linear-gradient(to right, rgb(191,155,48) ${progress}%, rgba(255,255,255,0.2) ${progress}%)` }}
                  data-testid={`media-story-seek-${index}`}
                />
              </div>
            </>
          ) : (
            <RevealImage
              src={getOptimizedUrl(item)}
              thumbSrc={getThumbUrl(item)}
              alt={item.title}
              className="w-full h-full group-hover:scale-[1.04] transition-transform duration-[1.2s] ease-out"
            />
          )}
        </div>
        <div className="absolute inset-0 border border-transparent group-hover:border-gold/15 transition-colors duration-700 pointer-events-none" />
      </div>

      <div className="w-full lg:w-1/3">
        <div className="w-8 h-[1px] bg-gold/60 mb-6" />
        <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-wide mb-4 leading-tight">
          {item.title}
        </h3>
        <p className="text-zinc-500 font-body font-light leading-relaxed text-sm md:text-base">
          {item.description}
        </p>
      </div>
    </motion.div>
    </div>
  );
}

export default function MediaStorySection({ media }) {
  return (
    <section className="pt-16 md:pt-20 pb-10 md:pb-14 px-6 md:px-12 lg:px-24" data-testid="media-story-section">
      <div className="max-w-7xl mx-auto space-y-20 lg:space-y-28">
        {media.map((item, i) => (
          <MediaBlock key={item.id} item={item} index={i} />
        ))}
      </div>
    </section>
  );
}
